import { type MouseEvent } from "react";
import { useRouter } from "@tanstack/react-router";

import {
  Pagination,
  PaginationPrevious,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationEllipsis,
  PaginationNext,
} from "@/components/ui/pagination";

export function IngredientPagination({
  currentPage,
  totalPages,
  onPageChange,
  getPageHref,
  className = "justify-end",
}: {
  currentPage: number;
  totalPages: number;
  onPageChange?: (page: number) => void;
  getPageHref?: (page: number) => string;
  className?: string;
}) {
  const router = useRouter();

  if (totalPages <= 1) return null;

  const handlePageClick =
    (page: number) => (event: MouseEvent<HTMLAnchorElement>) => {
      if (page === currentPage) {
        event.preventDefault();
        return;
      }

      if (getPageHref) {
        const shouldUseClientNavigation =
          event.button === 0 &&
          !event.metaKey &&
          !event.ctrlKey &&
          !event.shiftKey &&
          !event.altKey &&
          event.currentTarget.target !== "_blank";

        if (shouldUseClientNavigation) {
          event.preventDefault();
          void router.navigate({ href: getPageHref(page) });
        }

        return;
      }

      event.preventDefault();
      onPageChange?.(page);
    };

  const getVisiblePages = () => {
    const delta = 1;
    const range: number[] = [];
    const rangeWithDots: Array<number | "ellipsis-start" | "ellipsis-end"> = [];

    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, "ellipsis-start");
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push("ellipsis-end", totalPages);
    } else {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  return (
    <Pagination className={className}>
      <PaginationContent className="gap-0.5 sm:gap-1">
        <PaginationItem>
          <PaginationPrevious
            href={getPageHref?.(Math.max(1, currentPage - 1)) ?? "#"}
            onClick={handlePageClick(Math.max(1, currentPage - 1))}
            aria-disabled={currentPage <= 1}
            tabIndex={currentPage <= 1 ? -1 : undefined}
            className={
              currentPage <= 1
                ? "pointer-events-none opacity-50 max-sm:size-9 max-sm:p-0"
                : "max-sm:size-9 max-sm:p-0"
            }
          />
        </PaginationItem>

        {getVisiblePages().map((page) => (
          <PaginationItem key={page}>
            {page === "ellipsis-start" || page === "ellipsis-end" ? (
              <PaginationEllipsis />
            ) : (
              <PaginationLink
                href={getPageHref?.(page) ?? "#"}
                onClick={handlePageClick(page)}
                isActive={page === currentPage}
                aria-label={`Go to page ${page}`}
              >
                {page}
              </PaginationLink>
            )}
          </PaginationItem>
        ))}

        <PaginationItem>
          <PaginationNext
            href={getPageHref?.(Math.min(totalPages, currentPage + 1)) ?? "#"}
            onClick={handlePageClick(Math.min(totalPages, currentPage + 1))}
            aria-disabled={currentPage >= totalPages}
            tabIndex={currentPage >= totalPages ? -1 : undefined}
            className={
              currentPage >= totalPages
                ? "pointer-events-none opacity-50 max-sm:size-9 max-sm:p-0"
                : "max-sm:size-9 max-sm:p-0"
            }
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
}
